// src/pages/StudyPage.tsx
import React, { useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { Button } from "@/components/ui/button";
import UnifiedChatInterface from "@/components/ai-prayer/companion/UnifiedChatInterface";

interface StudyPageProps {
  seed?: string;
}

/**
 * What changed:
 * - Now accepts a seed prop from App.tsx (passed via React state)
 * - Priority: seed prop > location.state.prefill > URL ?prefill=
 * - Still gates by auth and preserves prefill on redirect
 */
export default function StudyPage({ seed = '' }: StudyPageProps) {
  const { isAuthenticated } = useAuth();
  const nav = useNavigate();
  const location = useLocation() as { state?: { prefill?: string } };
  const [params] = useSearchParams();

  // Priority: seed prop (from App state) > location.state > URL query
  const prefill = useMemo(() => {
    const result = (seed || location?.state?.prefill || params.get("prefill") || "").trim();
    console.log('[StudyPage] isAuthenticated:', isAuthenticated, 'prefill:', result);
    return result;
  }, [seed, location, params, isAuthenticated]);

  if (!isAuthenticated) {
    const goSignIn = () => {
      // This block is only used if you choose to route to /companion/study later.
      nav("/auth", {
        state: {
          redirectTo: "/companion/study",
          query: prefill ? `?prefill=${encodeURIComponent(prefill)}` : "",
        },
      });
    };
    return (
      <div className="mx-auto w-full max-w-xl p-6">
        <h1 className="text-xl font-semibold mb-2">Sign in to study with Abiel</h1>
        <p className="text-sm text-muted-foreground mb-4">
          We’ll save your verse so you can jump right in after you sign in.
        </p>
        <Button onClick={goSignIn}>Sign In</Button>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col">
      <UnifiedChatInterface
        mode="study"
        initialUserMessage={prefill ? `Study ${prefill}` : ""}
      />
    </div>
  );
}
